import { IconName } from '@/common/components/Icon/iconName';
import { AccessTargetType } from '@/security/types/AccessTarget';
import { DataAccess, DataAccessInfo, ProductDataAccess, ProjectDataAccess } from '@/security/types/DataAccess';

const DATA_ACCESS_PRODUCT_ICON: Record<ProductDataAccess, IconName> = {
  [ProductDataAccess.HideAllDesignData]: 'full-protect',
  [ProductDataAccess.HideModelReports]: 'protect',
  [ProductDataAccess.ShowAllData]: 'show-all',
};

export const PRODUCT_HIDE_ALL_DESIGN: DataAccessInfo = {
  id: ProductDataAccess.HideAllDesignData,
  title: 'Hide All Design Data',
  description: 'Hide proportion images, symmetry images, facet slope angles, reports and 3d model.',
  icon: DATA_ACCESS_PRODUCT_ICON[ProductDataAccess.HideAllDesignData],
};

export const PRODUCT_HIDE_MODEL_REPORTS: DataAccessInfo = {
  id: ProductDataAccess.HideModelReports,
  title: 'Hide Model & Reports',
  description:
    'Hide facet slopes angles, reports and 3d model. Cut can possibly be reproduced from proportions images.',
  icon: DATA_ACCESS_PRODUCT_ICON[ProductDataAccess.HideModelReports],
};

export const PRODUCT_SHOW_ALL_DATA: DataAccessInfo = {
  id: ProductDataAccess.ShowAllData,
  title: 'Show All Data',
  description: 'All Cut Data, including model and report is available to any user with access.',
  icon: DATA_ACCESS_PRODUCT_ICON[ProductDataAccess.ShowAllData],
};

const DATA_ACCESS_PROJECT_ICON: Record<ProjectDataAccess, IconName> = {
  [ProjectDataAccess.HideProjectSource]: 'protect',
  [ProjectDataAccess.ShowAllData]: 'show-all',
};

export const PROJECT_HIDE_PROJECT_SOURCE: DataAccessInfo = {
  id: ProjectDataAccess.HideProjectSource,
  title: 'Hide Project Source',
  description:
    'HPCarbon data file (.ox2z) is hidden. Only solution information from Cutwise and CarbonViewer is available to users.',
  icon: DATA_ACCESS_PROJECT_ICON[ProjectDataAccess.HideProjectSource],
};

export const PROJECT_HIDE_PROJECT_SOURCE_AND_SOLUTIONS: DataAccessInfo = {
  id: ProjectDataAccess.HideProjectSource,
  title: 'Hide Project Source And Solutions Design Data',
  description:
    'HPCarbon data file (.ox2z) is hidden. Proportion images, symmetry images, facet slope angles, reports and 3d model are hidden for solutions. Only solution information from Cutwise and CarbonViewer is available to users.',
  icon: DATA_ACCESS_PROJECT_ICON[ProjectDataAccess.HideProjectSource],
};

export const PROJECT_SHOW_ALL_DATA: DataAccessInfo = {
  id: ProjectDataAccess.ShowAllData,
  title: 'Show All Data',
  description:
    'HPCarbon data file (.ox2z) is available to any user with access. Solution parameters and proportions can be viewed in HPC.',
  icon: DATA_ACCESS_PROJECT_ICON[ProjectDataAccess.ShowAllData],
};

export const PROJECT_SHOW_ALL_DATA_AND_SOLUTIONS: DataAccessInfo = {
  id: ProjectDataAccess.ShowAllData,
  title: 'Show All Project And Solutions Data',
  description:
    'HPCarbon data file (.ox2z) is available to any user with access. Proportion images, symmetry images, facet slope angles, reports and 3d model are available for solutions. Solution parameters and proportions can be viewed in HPC.',
  icon: DATA_ACCESS_PROJECT_ICON[ProjectDataAccess.ShowAllData],
};

const PROJECT_DATA_ACCESS_AND_SOLUTIONS: Record<ProjectDataAccess, DataAccessInfo> = {
  [ProjectDataAccess.HideProjectSource]: PROJECT_HIDE_PROJECT_SOURCE_AND_SOLUTIONS,
  [ProjectDataAccess.ShowAllData]: PROJECT_SHOW_ALL_DATA_AND_SOLUTIONS,
};

const PROJECT_ONLY_DATA_ACCESS_TITLE: Record<ProjectDataAccess, string> = {
  [ProjectDataAccess.HideProjectSource]: PROJECT_HIDE_PROJECT_SOURCE.title,
  [ProjectDataAccess.ShowAllData]: 'Show All Project Data',
};

export function getDataAccessWithSolutions(dataAccess: DataAccess, type: AccessTargetType) {
  if (type !== 'project') {
    return undefined;
  }

  return PROJECT_DATA_ACCESS_AND_SOLUTIONS[dataAccess as ProjectDataAccess];
}

export function getDataAccessTitle(dataAccess: DataAccess, type: AccessTargetType) {
  if (type === 'project') {
    return PROJECT_ONLY_DATA_ACCESS_TITLE[dataAccess as ProjectDataAccess];
  }

  return getDataAccessById(dataAccess, type)?.title ?? '';
}

const PROJECT_DATA_ACCESS: DataAccessInfo[] = [PROJECT_HIDE_PROJECT_SOURCE, PROJECT_SHOW_ALL_DATA];

export const DRIVE_PROJECT_DATA_ACCESS: DataAccessInfo[] = [
  {
    id: ProjectDataAccess.HideProjectSource,
    title: 'Hide Project and Diamond data',
    description:
      'Hide HPCarbon data file (.ox2z) and all Cut Data (proportion images, symmetry images, facet slope angles, reports and 3d model)',
    icon: 'protect',
  },
  {
    id: ProjectDataAccess.ShowAllData,
    title: 'Show All Data',
    description: '',
    icon: 'show-all',
  },
];

const PRODUCT_DATA_ACCESS: DataAccessInfo[] = [
  PRODUCT_HIDE_ALL_DESIGN,
  PRODUCT_HIDE_MODEL_REPORTS,
  PRODUCT_SHOW_ALL_DATA,
];

export function getDataAccessesByType(type: AccessTargetType) {
  if (type === 'product') {
    return PRODUCT_DATA_ACCESS;
  }

  if (type === 'project') {
    return PROJECT_DATA_ACCESS;
  }

  if (type === 'driveProject' || type === 'driveFolder') {
    return DRIVE_PROJECT_DATA_ACCESS;
  }

  return [];
}

export function getDataAccessById(id: DataAccess, type: AccessTargetType) {
  return getDataAccessesByType(type).find((dataAccess) => dataAccess.id === id);
}

export function getDataAccessIcon(level: DataAccess, type: AccessTargetType) {
  if (type === 'product') {
    return DATA_ACCESS_PRODUCT_ICON[level];
  }

  if (type === 'project' || type === 'driveProject' || type === 'driveFolder') {
    return DATA_ACCESS_PROJECT_ICON[level as ProjectDataAccess];
  }

  return '' as IconName;
}
